import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

const SummaryCardComponent = ({
  label,
  value,
  subtext,
  change,
  changeType = 'positive', // 'positive' | 'negative' | 'neutral'
  icon: Icon,
  color = 'blue', // 'blue' | 'green' | 'amber' | 'purple' | 'red' | 'indigo'
  sparklineData = null, // array of numbers e.g. [10, 15, 8, 22, 18]
}) => {
  const borderColors = {
    blue: 'hover:border-[#0052FF]',
    green: 'hover:border-emerald-500',
    amber: 'hover:border-amber-500',
    purple: 'hover:border-purple-500',
    red: 'hover:border-red-500',
    indigo: 'hover:border-indigo-500',
  };

  const iconColors = {
    blue: 'text-[#0052FF] bg-blue-50',
    green: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    purple: 'text-purple-600 bg-purple-50',
    red: 'text-red-600 bg-red-50',
    indigo: 'text-indigo-600 bg-indigo-50',
  };

  const strokeColors = {
    blue: '#0052FF',
    green: '#10B981',
    amber: '#F59E0B',
    purple: '#8B5CF6',
    red: '#EF4444',
    indigo: '#6366F1',
  };

  // Convert sparkline array to SVG points
  let pointsStr = '';
  if (sparklineData && sparklineData.length > 1) {
    const min = Math.min(...sparklineData);
    const max = Math.max(...sparklineData);
    const range = max - min || 1;
    pointsStr = sparklineData
      .map((val, idx) => {
        const x = (idx / (sparklineData.length - 1)) * 100;
        const y = 30 - ((val - min) / range) * 24 - 3;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm transition-all relative overflow-hidden group ${borderColors[color] || borderColors.blue}`}
      data-testid="summary-kpi-card"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
        {Icon && (
          <div className={`p-2 rounded-lg ${iconColors[color] || iconColors.blue}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-2 mt-2">
        <p className="text-2xl md:text-3xl font-extrabold text-slate-900 font-mono tracking-tight">{value}</p>
        {change && (
          <span
            className={`text-xs font-semibold flex items-center font-mono ${
              changeType === 'positive'
                ? 'text-emerald-600'
                : changeType === 'negative'
                ? 'text-red-500'
                : 'text-slate-500'
            }`}
          >
            {changeType === 'positive' ? <ArrowUpRight className="w-3.5 h-3.5" /> : changeType === 'negative' ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
            {change}
          </span>
        )}
      </div>

      {subtext && <p className="text-[11px] text-slate-400 mt-1 font-medium">{subtext}</p>}

      {pointsStr && (
        <div className="absolute right-3 bottom-3 w-16 h-8 opacity-40 group-hover:opacity-100 transition-opacity pointer-events-none">
          <svg viewBox="0 0 100 30" className="w-full h-full stroke-2 fill-none" style={{ stroke: strokeColors[color] || '#0052FF' }}>
            <polyline points={pointsStr} />
          </svg>
        </div>
      )}
    </div>
  );
};

export default SummaryCardComponent;
